import React from 'react';
import type { SupportedLocale } from '../locales/locale';
import { cn } from '@/lib/utils';

interface LocaleFlagProps {
  locale: SupportedLocale;
  className?: string;
}

const flagSvgClassName = 'h-full w-full';

const EnglishFlag: React.FC = () => {
  const clipId = React.useId().replace(/:/g, '');

  return (
    <svg viewBox="0 0 60 30" className={flagSvgClassName} aria-hidden>
      <defs>
        <clipPath id={clipId}>
          <rect width="60" height="30" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <rect width="60" height="30" fill="#012169" />
        <path d="M0 0l60 30M60 0 0 30" stroke="#fff" strokeWidth="6" />
        <path d="M0 0l60 30M60 0 0 30" stroke="#C8102E" strokeWidth="4" />
        <path d="M30 0v30M0 15h60" stroke="#fff" strokeWidth="10" />
        <path d="M30 0v30M0 15h60" stroke="#C8102E" strokeWidth="6" />
      </g>
    </svg>
  );
};

const SpanishFlag: React.FC = () => (
  <svg viewBox="0 0 6 4" className={flagSvgClassName} aria-hidden>
    <rect width="6" height="4" fill="#AA151B" />
    <rect y="1" width="6" height="2" fill="#F1BF00" />
  </svg>
);

const FrenchFlag: React.FC = () => (
  <svg viewBox="0 0 3 2" className={flagSvgClassName} aria-hidden>
    <rect width="1" height="2" fill="#0055A4" />
    <rect x="1" width="1" height="2" fill="#fff" />
    <rect x="2" width="1" height="2" fill="#EF4135" />
  </svg>
);

const GermanFlag: React.FC = () => (
  <svg viewBox="0 0 5 3" className={flagSvgClassName} aria-hidden>
    <rect width="5" height="1" fill="#000" />
    <rect y="1" width="5" height="1" fill="#D00" />
    <rect y="2" width="5" height="1" fill="#FFCE00" />
  </svg>
);

const CatalanFlag: React.FC = () => (
  <svg viewBox="0 0 27 18" className={flagSvgClassName} aria-hidden>
    <rect width="27" height="18" fill="#FCDD09" />
    <rect y="2" width="27" height="2" fill="#DA121A" />
    <rect y="6" width="27" height="2" fill="#DA121A" />
    <rect y="10" width="27" height="2" fill="#DA121A" />
    <rect y="14" width="27" height="2" fill="#DA121A" />
  </svg>
);

const FLAG_BY_LOCALE: Record<SupportedLocale, React.FC> = {
  en: EnglishFlag,
  es: SpanishFlag,
  fr: FrenchFlag,
  de: GermanFlag,
  ca: CatalanFlag,
};

export const LocaleFlag: React.FC<LocaleFlagProps> = ({ locale, className }) => {
  const Flag = FLAG_BY_LOCALE[locale];

  return (
    <span
      className={cn('inline-block overflow-hidden rounded-[2px] shadow-[0_0_0_1px_rgba(39,39,41,0.15)]', className)}
      aria-hidden
    >
      <Flag />
    </span>
  );
};
